import { useEffect, useMemo, useState } from 'react';
import styles from './Modal.module.css';
import { StatusBadge } from '@/components/StatusBadge';
import { pslBand, useStoreDrilldown } from '@/hooks/useStoreDrilldown';
import type { DateRange } from '@/lib/dateRange';
import { count, days, pct } from '@/lib/format';
import iconSku from '@/assets/icons/chip-sku.svg';
import iconCity from '@/assets/icons/chip-city.svg';

export interface StoreDrilldownTarget {
  productId: string;
  skuLabel: string;
  city?: string | undefined;
  cityLabel?: string | undefined;
}

/**
 * Dark-store drill-down, opened from a SKU row's ⋮ action.
 *
 * Reproduces Figma 2169:19131 / 2169:8273 — the OSA and DOI tabs over a
 * store-grain table. Everything here is served by resources that already
 * existed (`stores`, `store_daily`); no new endpoint or table was added.
 */
export function StoreDrilldownModal({
  customerName,
  target,
  range,
  onClose,
}: {
  customerName: string;
  target: StoreDrilldownTarget;
  range: DateRange;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'OSA' | 'DOI'>('OSA');
  const q = useStoreDrilldown(customerName, target.productId, target.city, range, true);
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const maxPsl = useMemo(() => rows.reduce((m, r) => Math.max(m, r.psl ?? 0), 0), [rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return (
    <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.card} role="dialog" aria-modal="true" aria-label="Dark store detail">
        <div className={styles.head}>
          <div className={styles.headChips}>
            <span className={styles.chip}>
              <img className={styles.chipIcon} src={iconSku} alt="" />
              {target.skuLabel}
            </span>
            {target.cityLabel && (
              <span className={styles.chip}>
                <img className={styles.chipIcon} src={iconCity} alt="" />
                {target.cityLabel}
              </span>
            )}
          </div>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className={styles.tabs} role="tablist">
          {(['OSA', 'DOI'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`${styles.tab} ${tab === t ? styles.active : ''}`}
              onClick={() => { setTab(t); }}
            >
              {t}
            </button>
          ))}
        </div>

        <div className={styles.body}>
          {q.isLoading && <p className={styles.state}>Loading dark stores…</p>}
          {q.isError && <p className={styles.state}>{q.error.message}</p>}
          {!q.isLoading && !q.isError && rows.length === 0 && (
            <p className={styles.state}>No dark stores carried this SKU in the selected window.</p>
          )}

          {!q.isLoading && !q.isError && rows.length > 0 && (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Dark Stores</th>
                    {tab === 'OSA' ? (
                      <>
                        <th>OSA%</th>
                        <th>LS%</th>
                        <th>OSA in LS%</th>
                        <th className={styles.center}>PSL</th>
                      </>
                    ) : (
                      <>
                        <th>DOI</th>
                        <th>SOH</th>
                        <th>DRR</th>
                        <th className={styles.center}>PSL</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const band = pslBand(r.psl, maxPsl);
                    return (
                      <tr key={r.store_id}>
                        <td>{r.label}</td>
                        {tab === 'OSA' ? (
                          <>
                            <td>{pct(r.osa_pct)}</td>
                            <td>{pct(r.ls_pct)}</td>
                            <td>{pct(r.osa_in_ls_pct)}</td>
                            <td className={styles.center}>{band && <StatusBadge level={band} />}</td>
                          </>
                        ) : (
                          <>
                            <td>{days(r.doi)}</td>
                            <td>{count(r.soh)}</td>
                            <td>{r.drr == null ? '—' : r.drr.toFixed(1)}</td>
                            <td className={styles.center}>{band && <StatusBadge level={band} />}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className={styles.count}>
                {rows.length} dark stores · LS% is the share of observed days the SKU was listed;
                OSA in LS% is how often it was in stock while listed.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
